import { extractEncodedId } from '@ads/utils/extractEncodedId';
import { customEvent } from '@core/customEvent';
import { pruneNilValues } from '@core/object';
import { sendPixelTrackingWithSession as sendPixelTrackingWithSessionFn } from '@core/tracking';
import { ENGAGEMENT_CLICK_CTA, ENGAGEMENT_INTERACTABLE_VIEW, ENGAGEMENT_INTERACTABLE_VIEW_V2, } from '@core/widgets/tracking/endpoints/engagement';
import { customEmbedEvent, EventType } from '@embed/customEmbedEvent';
import { CHAT_CLICK_MODERATOR_LINK, FEED_CLICK_VIDEO, FEED_REDIRECT_FULLSCREEN_IMPRESSION, GATED_VIDEO_CORRECT_ACCESS_CODE, GATED_VIDEO_INCORRECT_ACCESS_CODE, VIDEO_PLAYER_CLICK_PAUSE, VIDEO_PLAYER_CLICK_RESUME, VIDEO_PLAYER_DISMISS, VIDEO_PLAYER_INIT, VIDEO_PLAYER_MUTE, VIDEO_PLAYER_NEXT_VIDEO, VIDEO_PLAYER_PREV_VIDEO, VIDEO_PLAYER_UNMUTE, } from '@player/tracking/endpoints';
import { AVA_FAQ_ENTRY_POINT_CLICK, AVA_FAQ_ENTRY_POINT_IMPRESSION, AVA_INPUT_ENTRY_POINT_SUBMIT, } from './endpoints/ava';
import { ENGAGEMENT_CREATE_POLL_IMPRESSION, ENGAGEMENT_CREATE_QUESTION_IMPRESSION, ENGAGEMENT_SUBMIT_POLL_RESPONSE, ENGAGEMENT_SUBMIT_QUESTION_RESPONSE, } from './endpoints/engagement';
/**
 *
 */
export const trackingFactory = ({ params, getPlayerNestedTrackingPayload, }) => {
    const sendPixelTrackingWithSession = (args) => {
        sendPixelTrackingWithSessionFn({
            ...args,
            data: {
                ...args.data,
                name: params?.name,
            },
            host: params?.api_host_tracking,
        });
    };
    const sendCustomEvent = (args) => {
        customEvent({
            ...args,
            data: {
                ...args.data,
                name: params?.name,
            },
        });
    };
    return {
        trackGatedVideoCorrectAccessCode: () => {
            sendPixelTrackingWithSession({
                endpoint: GATED_VIDEO_CORRECT_ACCESS_CODE,
                data: {},
            });
        },
        trackGatedVideoIncorrectAccessCode: () => {
            sendPixelTrackingWithSession({
                endpoint: GATED_VIDEO_INCORRECT_ACCESS_CODE,
                data: {},
            });
        },
        trackPlayerClose: (action) => {
            const { video, dismissAction } = action;
            if (!video) {
                return;
            }
            const { variant } = video;
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_DISMISS,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        dismiss_action: dismissAction,
                        variant,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
        },
        trackPlayerClickPause: ({ variant, videoId, data }) => {
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_CLICK_PAUSE,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: videoId,
                    }),
                },
            });
            sendCustomEvent({
                type: EventType.FW_VIDEO_CLICK_VIDEO,
                data: { status: 'paused', ...data },
            });
            sendCustomEvent({
                type: EventType.FW_VIDEO_USER_PAUSED,
                data: { ...data },
            });
        },
        trackPlayerClickResume: ({ variant, videoId, wasPlaying, data }) => {
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_CLICK_RESUME,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: videoId,
                    }),
                },
            });
            sendCustomEvent({
                type: EventType.FW_VIDEO_CLICK_VIDEO,
                data: { status: 'resumed', ...data },
            });
            if (wasPlaying) {
                sendCustomEvent({
                    type: EventType.FW_VIDEO_USER_RESUMED,
                    data: { ...data },
                });
            }
            else {
                sendCustomEvent({
                    type: EventType.FW_VIDEO_USER_PLAYED,
                    data: { ...data },
                });
            }
        },
        trackPlayerNextVideo: (action) => {
            const { video, nextAction } = action;
            if (video === null) {
                return;
            }
            const { variant } = video;
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_NEXT_VIDEO,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        next_action: nextAction,
                        variant,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
        },
        trackPlayerPrevVideo: (action) => {
            const { video, prevAction } = action;
            if (video === null) {
                return;
            }
            const { variant } = video;
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_PREV_VIDEO,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        prev_action: prevAction,
                        variant,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
        },
        // This may need to be moved into pixelVideoTracking
        trackMute: (action) => {
            const { video } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_MUTE,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
        },
        // This may need to be moved into pixelVideoTracking
        trackUnmute: (action) => {
            const { video } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_UNMUTE,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
        },
        trackCTAAction: (action) => {
            const { video, flowLinkId } = action;
            sendPixelTrackingWithSession({
                endpoint: ENGAGEMENT_CLICK_CTA,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        action_type: video?.action_type,
                        action_url: video?.action_url,
                        _video_id: extractEncodedId(video),
                        _flow_link_id: flowLinkId || undefined,
                    }),
                },
            });
        },
        trackMinimizedPlayerClick: ({ video }) => {
            sendPixelTrackingWithSession({
                endpoint: FEED_CLICK_VIDEO,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        ad_id: video?.ad_id,
                        autoplay: params?.autoplay,
                        canonical_url: video?.canonical_url,
                        variant: video?.variant,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
            customEmbedEvent({
                type: EventType.FW_VIDEO_CLICK_VIDEO,
                params,
                data: { video },
            });
            return {};
        },
        trackRedirectFullscreenImpression: (action) => {
            const { video } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: FEED_REDIRECT_FULLSCREEN_IMPRESSION,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
        },
        trackCreatePollImpression: (action) => {
            const { video, flowInteractionId } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: ENGAGEMENT_CREATE_POLL_IMPRESSION,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: extractEncodedId(video),
                        _flow_interaction_id: flowInteractionId,
                    }),
                },
            });
        },
        trackCreateQuestionImpression: (action) => {
            const { video, flowInteractionId } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: ENGAGEMENT_CREATE_QUESTION_IMPRESSION,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: extractEncodedId(video),
                        _flow_interaction_id: flowInteractionId,
                    }),
                },
            });
        },
        trackSubmitPollResponse: (action) => {
            const { video, interaction, answer, flowLinkId } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: ENGAGEMENT_SUBMIT_POLL_RESPONSE,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        interactive_interaction_response: answer,
                        _interactive_interaction_id: interaction?.id,
                        _video_id: extractEncodedId(video),
                        _flow_link_id: flowLinkId || undefined,
                    }),
                },
            });
        },
        trackSubmitQuestionResponse: (action, emailSupplied = false) => {
            const { video, interaction, answer, flowLinkId } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: ENGAGEMENT_SUBMIT_QUESTION_RESPONSE,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        email_supplied: emailSupplied,
                        email_required: interaction?.collect_email === 'required',
                        interactive_interaction_response: answer,
                        _interactive_interaction_id: interaction?.id,
                        _video_id: extractEncodedId(video),
                        _flow_link_id: flowLinkId || undefined,
                    }),
                },
            });
        },
        trackInteractableEngagement: (action) => {
            const { video, isV2 } = action;
            const { variant } = video ?? {};
            sendPixelTrackingWithSession({
                endpoint: isV2
                    ? ENGAGEMENT_INTERACTABLE_VIEW_V2
                    : ENGAGEMENT_INTERACTABLE_VIEW,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        variant,
                        _video_id: extractEncodedId(video),
                        _live_stream_id: video?.live_stream_id,
                        live_stream_status: video?.live_stream_status,
                    }),
                },
            });
        },
        trackPlayerInit: () => {
            sendPixelTrackingWithSession({
                endpoint: VIDEO_PLAYER_INIT,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                    }),
                },
            });
        },
        trackInteractiveVideoClick(action) {
            const { video, flowLink } = action;
            sendPixelTrackingWithSession({
                endpoint: ENGAGEMENT_CLICK_CTA,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        action_type: 'flow_interaction',
                        _flow_link_id: flowLink.id,
                        _video_id: extractEncodedId(video),
                    }),
                },
            });
        },
        trackModeratorLinkClick(action) {
            const { video, url } = action;
            sendPixelTrackingWithSession({
                endpoint: CHAT_CLICK_MODERATOR_LINK,
                data: {
                    event_properties: pruneNilValues({
                        ...getPlayerNestedTrackingPayload(),
                        _video_id: extractEncodedId(video),
                        url,
                    }),
                },
            });
        },
        trackAvaFAQEntryPointImpression: () => {
            sendPixelTrackingWithSession({
                endpoint: AVA_FAQ_ENTRY_POINT_IMPRESSION,
                data: {},
            });
        },
        trackAvaFAQEntryPointClick: (question) => {
            sendPixelTrackingWithSession({
                endpoint: AVA_FAQ_ENTRY_POINT_CLICK,
                data: { question },
            });
        },
        trackAvaInputEntryPointSubmit: (userQuestion) => {
            sendPixelTrackingWithSession({
                endpoint: AVA_INPUT_ENTRY_POINT_SUBMIT,
                data: { userQuestion },
            });
        },
    };
};
